import i18n from '~/localization/i18n';
import { Candidate, WorkAuthorization } from '~/services/CandidateService';
import { User } from '~/services/types/identity';

export enum CandidateProfileStatus {
    INCOMPLETE = 'INCOMPLETE',
    COMPLETE = 'COMPLETE',
}

export enum ProfileRequirementType {
    // profiles
    FULL_PROFILE = 'FULL_PROFILE',
    BE_DISCOVERED = 'BE_DISCOVERED',
    VFAIR_PROFILE = 'VFAIR_PROFILE',
    VFAIR_PROFILE_MINIMAL = 'VFAIR_PROFILE_MINIMAL',
    APPLY_JOB_PROFILE = 'APPLY_JOB',
    // welcome flow
    NEW_USER = 'NEW_USER', // = FULL_PROFILE - (FIRST_NAME, LAST_NAME): needed to detect new users
    WELCOME_FLOW = 'WELCOME_FLOW',
    WELCOME_FLOW_STEP0 = 'WELCOME_FLOW_STEP0',
    WELCOME_FLOW_STEP1 = 'WELCOME_FLOW_STEP1',
    WELCOME_FLOW_STEP2 = 'WELCOME_FLOW_STEP2',
    WELCOME_FLOW_STEP3 = 'WELCOME_FLOW_STEP3',
}

type ProfileRequirement = {
    [key in ProfileRequirementType]?: boolean;
};

const PROFREG = ProfileRequirementType;

const newProfileRequirement = (requirements: ProfileRequirementType[]): ProfileRequirement => {
    return requirements.reduce((acc, cur) => {
        acc[cur] = true;
        return acc;
    }, {} as ProfileRequirement);
};

export enum CandidateProfileActionId {
    FIRST_NAME = 'FIRST_NAME',
    LAST_NAME = 'LAST_NAME',
    GENDER = 'GENDER',
    PRONOUN = 'PRONOUN',
    DISABILITY = 'DISABILITY',
    NATIONALITY = 'NATIONALITY',
    LOCATION = 'LOCATION',
    WORK_AUTHORIZATION = 'WORK_AUTHORIZATION',
    SUMMARY = 'SUMMARY',

    SKILLS = 'SKILLS',
    LANGUAGES = 'LANGUAGES',
    WORK_EXPERIENCE = 'WORK_EXPERIENCE',
    STUDY_PROGRAMS = 'STUDY_PROGRAMS',
    STUDY_PROGRAMS_WELCOME_SCHOOL = 'STUDY_PROGRAMS_WELCOME_SCHOOL',
    MEMBERSHIPS = 'MEMBERSHIPS',
    PREFERENCES = 'PREFERENCES',
    LINKS = 'LINKS',

    PROFILE_PICTURE = 'PROFILE_PICTURE',
}

export interface CandidateProfileAction {
    id: CandidateProfileActionId;
    label: string;
    status: CandidateProfileStatus;
    priority?: number;
}

export interface CandidateProfileScore {
    // The progress in percent
    progress: number;
    status: CandidateProfileStatus;
    actions: CandidateProfileAction[];
}

export interface ProfileRequirementAction {
    requirement?: ProfileRequirement;
    score: number;
    id: CandidateProfileActionId;
    priority?: number;
    eval: (
        candidate: Partial<Candidate>,
        user: Partial<User>,
        workAuthorizations: Partial<WorkAuthorization>[]
    ) => boolean;
}

export class CandidateProfileScorer {
    candidate: Partial<Candidate>;
    user: Partial<User>;
    workAuthorizations: Partial<WorkAuthorization>[];

    constructor(
        candidate: Partial<Candidate>,
        user: Partial<User>,
        workAuthorizations: Partial<WorkAuthorization>[]
    ) {
        this.candidate = candidate;
        this.user = user;
        this.workAuthorizations = workAuthorizations;
    }

    calculateScore(requirement?: ProfileRequirementType): CandidateProfileScore {
        const score: CandidateProfileScore = {
            progress: 0,
            status: CandidateProfileStatus.INCOMPLETE,
            actions: [],
        };

        let totalScore = 0;
        this.getActions(requirement).map(action => {
            totalScore += action.score;
            const actionResult = this.calculateAction(action);
            if (actionResult.status === CandidateProfileStatus.COMPLETE) {
                score.progress += action.score;
            } else {
                score.actions.push(actionResult);
            }
        });

        score.progress = Math.round((score.progress / totalScore) * 100);
        score.status =
            score.progress === 100
                ? CandidateProfileStatus.COMPLETE
                : CandidateProfileStatus.INCOMPLETE;

        return score;
    }

    private calculateAction(action: ProfileRequirementAction): CandidateProfileAction {
        const status = action.eval(this.candidate, this.user, this.workAuthorizations)
            ? CandidateProfileStatus.INCOMPLETE
            : CandidateProfileStatus.COMPLETE;
        return {
            ...action,
            status,
            label: i18n.t(`profile.score.fields.${action.id}`).toString(),
        };
    }

    getProfileScore(): CandidateProfileScore {
        return this.calculateScore(ProfileRequirementType.FULL_PROFILE);
    }

    getVFairProfileScore(minimum = false): CandidateProfileScore {
        return this.calculateScore(
            minimum
                ? ProfileRequirementType.VFAIR_PROFILE_MINIMAL
                : ProfileRequirementType.VFAIR_PROFILE
        );
    }

    getApplyJobProfileScore(): CandidateProfileScore {
        return this.calculateScore(ProfileRequirementType.APPLY_JOB_PROFILE);
    }

    getBeDiscoveredScore(): CandidateProfileScore {
        return this.calculateScore(ProfileRequirementType.BE_DISCOVERED);
    }

    getWelcomeFlowScore(): CandidateProfileScore {
        return this.calculateScore(ProfileRequirementType.WELCOME_FLOW);
    }

    getWelcomeFlowStep0Score(): CandidateProfileScore {
        return this.calculateScore(ProfileRequirementType.WELCOME_FLOW_STEP0);
    }

    getWelcomeFlowStep1Score(): CandidateProfileScore {
        return this.calculateScore(ProfileRequirementType.WELCOME_FLOW_STEP1);
    }

    getWelcomeFlowStep2Score(): CandidateProfileScore {
        return this.calculateScore(ProfileRequirementType.WELCOME_FLOW_STEP2);
    }

    getWelcomeFlowStep3Score(): CandidateProfileScore {
        return this.calculateScore(ProfileRequirementType.WELCOME_FLOW_STEP3);
    }

    getNewUserScore(): CandidateProfileScore {
        return this.calculateScore(ProfileRequirementType.NEW_USER);
    }

    getActions(requirement?: ProfileRequirementType): ProfileRequirementAction[] {
        return this.actions.filter(
            action => !requirement || (action.requirement && action.requirement[requirement])
        );
    }

    getCandidateProfileActions(requirement?: ProfileRequirementType): CandidateProfileAction[] {
        return this.getActions(requirement).map(action => this.calculateAction(action));
    }

    private actions: ProfileRequirementAction[] = [
        // Required actions for all score calculations
        {
            id: CandidateProfileActionId.FIRST_NAME,
            requirement: newProfileRequirement([
                PROFREG.FULL_PROFILE,
                PROFREG.BE_DISCOVERED,
                PROFREG.VFAIR_PROFILE,
                PROFREG.VFAIR_PROFILE_MINIMAL,
                PROFREG.APPLY_JOB_PROFILE,
                PROFREG.WELCOME_FLOW,
                PROFREG.WELCOME_FLOW_STEP0,
            ]),
            score: 5,
            eval: (c, user) => !user.firstName,
            priority: 100,
        },
        {
            id: CandidateProfileActionId.LAST_NAME,
            requirement: newProfileRequirement([
                PROFREG.FULL_PROFILE,
                PROFREG.VFAIR_PROFILE_MINIMAL,
                PROFREG.WELCOME_FLOW,
                PROFREG.WELCOME_FLOW_STEP0,
            ]),
            score: 5,
            eval: (c, user) => !user.lastName,
        },
        {
            id: CandidateProfileActionId.GENDER,
            requirement: newProfileRequirement([
                PROFREG.FULL_PROFILE,
                PROFREG.VFAIR_PROFILE,
                PROFREG.VFAIR_PROFILE_MINIMAL,
                PROFREG.BE_DISCOVERED,
                PROFREG.NEW_USER,
            ]),
            score: 5,
            eval: c => !c.gender || c.gender === 'U',
        },
        {
            id: CandidateProfileActionId.PRONOUN,
            score: 5,
            eval: c => !c.pronoun,
        },
        {
            id: CandidateProfileActionId.DISABILITY,
            score: 5,
            eval: c => !c.disability,
        },
        {
            id: CandidateProfileActionId.NATIONALITY,
            requirement: newProfileRequirement([
                PROFREG.FULL_PROFILE,
                PROFREG.BE_DISCOVERED,
                PROFREG.VFAIR_PROFILE,
                PROFREG.APPLY_JOB_PROFILE,
                PROFREG.WELCOME_FLOW,
                PROFREG.WELCOME_FLOW_STEP1,
                PROFREG.NEW_USER,
            ]),
            score: 10,
            eval: c => !c.nationalities || c.nationalities.length === 0,
            priority: 90,
        },
        {
            id: CandidateProfileActionId.LOCATION,
            requirement: newProfileRequirement([
                PROFREG.VFAIR_PROFILE,
                PROFREG.VFAIR_PROFILE_MINIMAL,
                PROFREG.BE_DISCOVERED,
                PROFREG.WELCOME_FLOW,
                PROFREG.WELCOME_FLOW_STEP1,
            ]),
            score: 10,
            eval: c => !c.country,
            priority: 85,
        },
        {
            id: CandidateProfileActionId.WORK_AUTHORIZATION,
            requirement: newProfileRequirement([
                PROFREG.FULL_PROFILE,
                PROFREG.VFAIR_PROFILE,
                PROFREG.BE_DISCOVERED,
                PROFREG.NEW_USER,
            ]),
            score: 5,
            eval: (c, user, wa) => !wa || wa.length === 0,
        },
        {
            id: CandidateProfileActionId.SUMMARY,
            requirement: newProfileRequirement([PROFREG.FULL_PROFILE, PROFREG.NEW_USER]),
            score: 10,
            eval: c => !c.summary,
        },

        {
            id: CandidateProfileActionId.SKILLS,
            requirement: newProfileRequirement([
                PROFREG.FULL_PROFILE,
                PROFREG.VFAIR_PROFILE,
                PROFREG.BE_DISCOVERED,
                PROFREG.NEW_USER,
            ]),
            score: 10,
            eval: c => !c.skills || c.skills.length === 0,
            priority: 80,
        },
        {
            id: CandidateProfileActionId.LANGUAGES,
            requirement: newProfileRequirement([
                PROFREG.FULL_PROFILE,
                PROFREG.VFAIR_PROFILE,
                PROFREG.BE_DISCOVERED,
                PROFREG.NEW_USER,
            ]),
            score: 10,
            eval: c => !c.languages || c.languages.length === 0,
        },
        {
            id: CandidateProfileActionId.WORK_EXPERIENCE,
            requirement: newProfileRequirement([PROFREG.FULL_PROFILE, PROFREG.NEW_USER]),
            score: 10,
            eval: c => !c.workExperiences || c.workExperiences.length === 0,
        },
        {
            id: CandidateProfileActionId.STUDY_PROGRAMS,
            requirement: newProfileRequirement([
                PROFREG.FULL_PROFILE,
                PROFREG.BE_DISCOVERED,
                PROFREG.VFAIR_PROFILE,
                PROFREG.APPLY_JOB_PROFILE,
                PROFREG.NEW_USER,
            ]),
            score: 10,
            eval: c =>
                !c.studyPrograms ||
                c.studyPrograms.length === 0 ||
                !c.studyPrograms.some(
                    x =>
                        (x.studyProgram?.isVerified ||
                            (x.studyProgram?.level && x.studyProgram?.fields?.length)) &&
                        x.start &&
                        x.end
                ),
            priority: 60,
        },
        {
            id: CandidateProfileActionId.STUDY_PROGRAMS_WELCOME_SCHOOL,
            requirement: newProfileRequirement([PROFREG.WELCOME_FLOW, PROFREG.WELCOME_FLOW_STEP2]),
            score: 10,
            eval: c => {
                if (!c.studyPrograms) {
                    return true;
                }

                return !c.studyPrograms?.some(
                    csp =>
                        (csp.studyProgram?.schoolId || csp.studyProgram?.customSchoolName) &&
                        (csp.studyProgram?.isVerified ||
                            (csp.studyProgram?.level && csp.studyProgram?.fields?.length)) &&
                        csp.end
                );
            },
            priority: 60,
        },
        {
            id: CandidateProfileActionId.MEMBERSHIPS,
            score: 10,
            eval: c => !c.memberships || c.memberships.length === 0,
            priority: 60,
        },

        {
            id: CandidateProfileActionId.PREFERENCES,
            requirement: newProfileRequirement([
                PROFREG.FULL_PROFILE,
                PROFREG.BE_DISCOVERED,
                PROFREG.VFAIR_PROFILE,
                PROFREG.APPLY_JOB_PROFILE,
                PROFREG.WELCOME_FLOW,
                PROFREG.WELCOME_FLOW_STEP3,
                PROFREG.NEW_USER,
            ]),
            score: 10,
            eval: c =>
                !c.preferences ||
                !c.preferences.careerFields ||
                c.preferences.careerFields.length === 0,
            priority: 70,
        },
        {
            id: CandidateProfileActionId.LINKS,
            score: 10,
            eval: c => !c.links || c.links.length === 0,
            priority: 85,
        },
        // {
        //     id: CandidateProfileActionId.PROFILE_PICTURE,
        //     requirement: newProfileRequirement([PROFREG.FULL_PROFILE]),
        //     score: 10,
        //     eval: (c, user) => !user.avatar,
        // },
    ];
}
